/*
 *
 * Script for accessibility
 *
 */

import { setCookie, getCookie, deleteCookie } from './cookies';

export const accessibilityWidget = () => {
    fontSizeAccessibility();
    contrastMode();
    saturationMode();
    monochromeMode();
    cursorMode();
    accessibilityReset();

    const toggleButtons = document.querySelectorAll('.bn-accessibility__button');
    const widget = document.querySelector('.bn-accessibility__widget');
    const openClass = '--open';

    if (!widget) return;

    const updateWidgetState = (open) => {
        if (open) {
            widget.classList.add(openClass);
            setCookie('accessibilityWidget', 'open', 7);
            localStorage.setItem('accessibilityWidget', 'open');
        } else {
            widget.classList.remove(openClass);
            deleteCookie('accessibilityWidget');
            localStorage.removeItem('accessibilityWidget');
        }
    };

    const openWidget = () => updateWidgetState(true);
    const closeWidget = () => updateWidgetState(false);

    toggleButtons.forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            widget.classList.contains(openClass) ? closeWidget() : openWidget();
        });
    });

    // Focus on elements inside widget
    const focusElements = widget.querySelectorAll(
        '.close-accessibility-widget, .button-accessibility-reset'
    );

    focusElements.forEach(el => {
        el.addEventListener('focus', () => {
            setTimeout(() => openWidget(), 0);
        });
    });

    const closeButton = widget.querySelector('.close-accessibility-widget');
    if (closeButton) {
        closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            closeWidget();
        });
    }

    const isOpen = () =>
        getCookie('accessibilityWidget') === 'open' ||
        localStorage.getItem('accessibilityWidget') === 'open';

    if (isOpen()) updateWidgetState(true);

    document.addEventListener('focusin', (e) => {
        if (!widget.contains(e.target) && ![...toggleButtons].includes(e.target)) {
            closeWidget();
        }
    });
};

// ---------------------------
// Font size
// ---------------------------
export const fontSizeAccessibility = () => {
    const fontSizeIncreaseButtons = document.querySelectorAll('.button-font-size-increase');
    const fontSizeDecreaseButtons = document.querySelectorAll('.button-font-size-decrease');
    const wrapper = document.querySelector('.bn-accessibility__widget--option.button-font-size-toggle');
    const slider = wrapper?.querySelector('.option-switch');
    const currentLabel = wrapper?.querySelector('.font-size-current');

    const defaultFontSize = 10;
    const minFontSize = 8;
    const maxFontSize = 13;

    const getFontSize = () => {
        const storedSize = getCookie('fontSize') || localStorage.getItem('fontSize');
        return storedSize ? parseInt(storedSize) : defaultFontSize;
    };

    const updateLabel = (size) => {
        if (!currentLabel) return;
        const percent = Math.round(((size - defaultFontSize) / defaultFontSize) * 100);
        currentLabel.textContent = percent === 0 ? "Standard" : (percent > 0 ? "+" : "") + percent + "%";
    };

    const updateFontSize = (newSize) => {
        if (newSize === defaultFontSize) {
            document.documentElement.style.removeProperty('--font-size');
            deleteCookie('fontSize');
            localStorage.removeItem('fontSize');
            wrapper?.classList.remove('--checkbox-active');
        } else {
            document.documentElement.style.setProperty('--font-size', newSize + 'px');
            setCookie('fontSize', newSize.toString(), 7);
            localStorage.setItem('fontSize', newSize.toString());
            wrapper?.classList.add('--checkbox-active');
        }
        updateLabel(newSize);
        updateButtonClasses();
    };

    const updateButtonClasses = () => {
        const fontSize = getFontSize();
        fontSizeIncreaseButtons.forEach(btn => btn.classList.toggle('--active', fontSize > defaultFontSize));
        fontSizeDecreaseButtons.forEach(btn => btn.classList.toggle('--active', fontSize < defaultFontSize));
    };

    const currentSize = getFontSize();
    if (currentSize !== defaultFontSize) {
        document.documentElement.style.setProperty('--font-size', currentSize + 'px');
        wrapper?.classList.add('--checkbox-active');
    }
    updateLabel(currentSize);
    updateButtonClasses();

    const setupButtonListeners = (buttons, change) => {
        buttons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const newSize = getFontSize() + change;
                if (newSize >= minFontSize && newSize <= maxFontSize) updateFontSize(newSize);
            });
        });
    };

    setupButtonListeners(fontSizeIncreaseButtons, 1);
    setupButtonListeners(fontSizeDecreaseButtons, -1);

    slider?.addEventListener('click', () => {
        updateFontSize(wrapper.classList.contains('--checkbox-active') ? defaultFontSize : defaultFontSize + 1);
    });
};

// ---------------------------
// Contrast mode
// ---------------------------
export const contrastMode = () => {
    const contrastTogglers = document.querySelectorAll('.button-contrast-toggle');
    const body = document.body;
    const isContrastMode = getCookie('contrastMode') === 'true' || localStorage.getItem('contrastMode') === 'true';

    body.classList.toggle('contrast-mode', isContrastMode);

    const syncUI = () => {
        contrastTogglers.forEach(btn => {
            const active = body.classList.contains('contrast-mode');
            btn.classList.toggle('--checkbox-active', active);
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
    };

    syncUI();

    contrastTogglers.forEach(btn => {
        btn.addEventListener('click', e => {
            e.preventDefault();
            const active = body.classList.toggle('contrast-mode');
            setCookie('contrastMode', active, 7);
            localStorage.setItem('contrastMode', active);
            syncUI();
        });
    });
};

// ---------------------------
// Saturation mode
// ---------------------------
export const saturationMode = () => {
    const togglers = document.querySelectorAll('.button-saturation-toggle');
    const html = document.documentElement;

    const isActive = getCookie('saturationMode') === 'true' || localStorage.getItem('saturationMode') === 'true';

    if (isActive) {
        html.classList.add('saturation-mode');
        html.classList.remove('monochrome-mode');
        setCookie('monochromeMode', false, 7);
        localStorage.setItem('monochromeMode', false);
    }

    const syncUI = () => {
        const saturationActive = html.classList.contains('saturation-mode');

        togglers.forEach(btn => {
            btn.classList.toggle('--checkbox-active', saturationActive);
            btn.setAttribute('aria-pressed', saturationActive ? 'true' : 'false');
        });

        const monoTogglers = document.querySelectorAll('.button-monochrome-toggle');
        monoTogglers.forEach(btn => {
            btn.classList.remove('--checkbox-active');
            btn.setAttribute('aria-pressed', 'false');
        });
    };

    syncUI();

    togglers.forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const nowActive = html.classList.toggle('saturation-mode');

            if (nowActive) {
                html.classList.remove('monochrome-mode');
                setCookie('monochromeMode', false, 7);
                localStorage.setItem('monochromeMode', false);
            }

            setCookie('saturationMode', nowActive, 7);
            localStorage.setItem('saturationMode', nowActive);
            syncUI();
        });
    });
};

// ---------------------------
// Monochrome mode
// ---------------------------
export const monochromeMode = () => {
    const togglers = document.querySelectorAll('.button-monochrome-toggle');
    const html = document.documentElement;

    const isActive = getCookie('monochromeMode') === 'true' || localStorage.getItem('monochromeMode') === 'true';

    if (isActive) {
        html.classList.add('monochrome-mode');
        html.classList.remove('saturation-mode');
        setCookie('saturationMode', false, 7);
        localStorage.setItem('saturationMode', false);
    }

    const syncUI = () => {
        const monochromeActive = html.classList.contains('monochrome-mode');

        togglers.forEach(btn => {
            btn.classList.toggle('--checkbox-active', monochromeActive);
            btn.setAttribute('aria-pressed', monochromeActive ? 'true' : 'false');
        });

        const satTogglers = document.querySelectorAll('.button-saturation-toggle');
        satTogglers.forEach(btn => {
            btn.classList.remove('--checkbox-active');
            btn.setAttribute('aria-pressed', 'false');
        });
    };

    syncUI();

    togglers.forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const nowActive = html.classList.toggle('monochrome-mode');

            if (nowActive) {
                html.classList.remove('saturation-mode');
                setCookie('saturationMode', false, 7);
                localStorage.setItem('saturationMode', false);
            }

            setCookie('monochromeMode', nowActive, 7);
            localStorage.setItem('monochromeMode', nowActive);
            syncUI();
        });
    });
};

// ---------------------------
// Cursor mode
// ---------------------------
export const cursorMode = () => {
    const togglers = document.querySelectorAll('.button-cursor-toggle');
    const html = document.documentElement;
    const isActive = getCookie('cursorMode') === 'true' || localStorage.getItem('cursorMode') === 'true';

    html.classList.toggle('cursor-mode', isActive);

    const syncUI = () => {
        togglers.forEach(btn => {
            const active = html.classList.contains('cursor-mode');
            btn.classList.toggle('--checkbox-active', active);
            btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
    };

    syncUI();

    togglers.forEach(btn => {
        btn.addEventListener('click', e => {
            e.preventDefault();
            const active = html.classList.toggle('cursor-mode');
            setCookie('cursorMode', active, 7);
            localStorage.setItem('cursorMode', active);
            syncUI();
        });
    });
};

// ---------------------------
// Reset
// ---------------------------
export const accessibilityReset = () => {
    const resetBtn = document.querySelector('.button-accessibility-reset');
    if (!resetBtn) return;

    const HTML_CLASSES = [
        'saturation-mode',
        'monochrome-mode',
        'cursor-mode',
    ];

    const BODY_CLASSES = [
        'contrast-mode',
    ];

    const STORAGE_KEYS = [
        'saturationMode',
        'monochromeMode',
        'contrastMode',
        'cursorMode',
        'fontSize'
    ];

    resetBtn.addEventListener('click', e => {
        e.preventDefault();

        document.documentElement.classList.remove(...HTML_CLASSES);
        document.body.classList.remove(...BODY_CLASSES);

        STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
        STORAGE_KEYS.forEach(key => setCookie(key, false, -1));

        document.querySelectorAll('.bn-accessibility__widget .--checkbox-active').forEach(el => {
            el.classList.remove('--checkbox-active');
            if (el.classList.contains('bn-accessibility__widget--option')) {
                el.setAttribute('aria-pressed', 'false');
            }
        });

        const wrapper = document.querySelector('.bn-accessibility__widget--option.button-font-size-toggle');
        if (wrapper) {
            const currentLabel = wrapper.querySelector('.font-size-current');
            document.documentElement.style.removeProperty('--font-size');
            wrapper.classList.remove('--checkbox-active');
            if (currentLabel) currentLabel.textContent = "Standard";
        }
    });
};
